/*
 * Страница Kafka (Resource kafka-cluster): «Узлы Kafka» — поды брокеров и контроллеров по
 * пулам Strimzi (роль, узел Kubernetes, адрес), «Топики» — KafkaTopic плюс топики, видимые
 * только по метрикам.
 */
import { TableCell, TableRow, Tooltip } from '@material-ui/core';
import { StatusError, StatusOK } from '@backstage/core-components';
import { KafkaNode } from '../../../lib/datastores';
import { KafkaIcon } from '../../icons';
import {
  CardTitle,
  DatastoreCard,
  DsTable,
  Fact,
  PodStatus,
  ReadyStatus,
  plural,
  useDsStyles,
} from './shared';
import { KafkaData, useKafkaState } from './useDatastoreData';

const NodeRow = ({ n }: { n: KafkaNode }) => {
  const classes = useDsStyles();
  return (
    <TableRow>
      <TableCell className={classes.nowrap}>
        <b>{n.name}</b>
        {n.nodeId !== undefined && (
          <span className={classes.muted}>{` · id ${n.nodeId}`}</span>
        )}
      </TableCell>
      <TableCell className={classes.nowrap}>{n.pool ?? '—'}</TableCell>
      <TableCell className={classes.nowrap}>
        {n.roles.map(r => (
          <span key={r} className={classes.tag}>
            {r}
          </span>
        ))}
      </TableCell>
      <TableCell className={classes.nowrap}>
        <PodStatus ready={n.ready} phase={n.phase} />
      </TableCell>
      <TableCell className={classes.mono}>{n.podIp ?? '—'}</TableCell>
      <TableCell className={classes.nowrap}>{n.node ?? '—'}</TableCell>
      <TableCell className={classes.muted}>{n.restarts}</TableCell>
    </TableRow>
  );
};

export const KafkaNodesCard = () => {
  const state = useKafkaState();
  const classes = useDsStyles();
  return (
    <DatastoreCard<KafkaData>
      title={
        <CardTitle icon={<KafkaIcon fontSize="small" />} text="Узлы Kafka" />
      }
      subheader={d =>
        `${d.nodes.length} ${plural(
          d.nodes.length,
          'узел',
          'узла',
          'узлов',
        )} в ${d.pools.length} ${plural(
          d.pools.length,
          'пуле',
          'пулах',
          'пулах',
        )} · KRaft`
      }
      state={state}
      errorTitle="Не удалось загрузить узлы Kafka"
    >
      {d => (
        <>
          <div className={classes.facts}>
            <span>
              {d.summary.ready ? (
                <StatusOK>Ready</StatusOK>
              ) : (
                <StatusError>Not ready</StatusError>
              )}
            </span>
            {d.summary.version && (
              <Fact label="Kafka" value={d.summary.version} />
            )}
            {d.summary.metadataVersion && (
              <Fact label="metadata" value={d.summary.metadataVersion} />
            )}
            {d.summary.bootstrap.map(b => (
              <Fact
                key={b.name}
                label={`bootstrap ${b.name}`}
                value={<span className={classes.mono}>{b.servers}</span>}
              />
            ))}
          </div>
          <div className={classes.facts}>
            {d.pools.map(p => (
              <Fact
                key={p.name}
                label={p.name}
                value={[
                  p.roles.join('+'),
                  `${p.replicas} ${p.replicas === 1 ? 'реплика' : 'реплик'}`,
                  p.storage && `диск ${p.storage}`,
                  (p.cpu || p.memory) &&
                    [p.cpu && `${p.cpu} CPU`, p.memory]
                      .filter(Boolean)
                      .join(' · '),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            ))}
          </div>
          <DsTable
            head={[
              'Узел',
              'Пул',
              'Роли',
              'Под',
              'Адрес пода',
              'Узел Kubernetes',
              'Перезапуски',
            ]}
            empty={
              d.nodes.length === 0
                ? 'Подов узлов пока нет — кластер разворачивается'
                : undefined
            }
          >
            {d.nodes.map(n => (
              <NodeRow key={n.name} n={n} />
            ))}
          </DsTable>
          <div className={classes.foot}>
            Источник: Kafka и KafkaNodePool Strimzi, поды namespace datastores
            через GitLab Agent.
          </div>
        </>
      )}
    </DatastoreCard>
  );
};

export const KafkaTopicsCard = () => {
  const state = useKafkaState();
  const classes = useDsStyles();
  return (
    <DatastoreCard<KafkaData>
      title={<CardTitle icon={<KafkaIcon fontSize="small" />} text="Топики" />}
      subheader={d =>
        `${d.topics.length} ${plural(
          d.topics.length,
          'топик',
          'топика',
          'топиков',
        )}`
      }
      state={state}
      errorTitle="Не удалось загрузить топики Kafka"
    >
      {d => (
        <>
          <DsTable
            head={[
              'Топик',
              'Партиции',
              'Реплики',
              'Хранение',
              'Политика',
              'Управление',
              'Состояние',
            ]}
            empty={d.topics.length === 0 ? 'Топиков пока нет' : undefined}
          >
            {d.topics.map(t => (
              <TableRow key={t.name}>
                <TableCell className={classes.nowrap}>
                  <b className={classes.mono}>{t.name}</b>
                </TableCell>
                <TableCell>{t.partitions ?? '—'}</TableCell>
                <TableCell>{t.replicas ?? '—'}</TableCell>
                <TableCell className={classes.nowrap}>
                  {t.retention ?? '—'}
                </TableCell>
                <TableCell className={classes.nowrap}>
                  {t.cleanupPolicy ?? '—'}
                </TableCell>
                <TableCell className={classes.muted}>
                  {t.managed ? 'KafkaTopic' : 'вне KafkaTopic (по метрикам)'}
                </TableCell>
                <TableCell className={classes.nowrap}>
                  <ReadyStatus ready={t.ready} />
                </TableCell>
              </TableRow>
            ))}
          </DsTable>
          <div className={classes.foot}>
            Топики — ресурсы KafkaTopic Strimzi; топики, созданные приложениями
            напрямую, приходят из метрик kafka-exporter без реплик и настроек
            {d.metricsError && (
              <Tooltip title={d.metricsError.message}>
                <span>
                  {' '}
                  · Prometheus недоступен, показаны только KafkaTopic
                </span>
              </Tooltip>
            )}
            .
          </div>
        </>
      )}
    </DatastoreCard>
  );
};
